import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { cloneDeep, isEmpty } from 'lodash';
import { distinctUntilChanged, finalize, take } from 'rxjs/operators';

import { DialogYesNoComponent } from 'src/app/shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { SpinnerService } from 'src/app/shared/spinner/spinner.service';

import { BuyerService } from '../../../core/api/buyer.service';
import { DocumentService } from '../../../core/api/document.service';
import { SiteService } from '../../../core/api/site.service';
import { FormatHelperService } from '../../../core/helper/format-helper.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { RegexConstants } from '../../../shared/constants/regex.constants';
import { Document, Site } from '../../../shared/models';
import * as fromApp from '../../../store';
import { selectDocumentTypes } from '../../../store/document/document.selectors';
import {
  updateSiteFailure,
  updateSiteStep,
  updateSubmitDocumentationSiteSuccessAndNavigate,
} from '../../../store/site/site.actions';
import { selectActiveSite } from '../../../store/site/site.selectors';

@Component({
  selector: 'fc-information-documentation',
  templateUrl: './information-documentation-page.component.html',
  styleUrls: ['./information-documentation-page.component.scss'],
})
export class InformationDocumentationComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  submitInfoForm: UntypedFormGroup;

  deedTypeId = 1;
  taxTypeId = 1;
  activeSite: Site = null;
  valuePerYear: string;

  docTypes = [];
  existingDocs: Document[] = [];
  showNameList: boolean = false;
  showForestryFirm: boolean = false;
  showMortgage: boolean = false;
  isFormDirty: boolean = false;

  getOpenAssetURL = BuyerService.getOpenAssetURL;

  siteId: number;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly documentService: DocumentService,
    private formBuilder: UntypedFormBuilder,
    readonly formatHelper: FormatHelperService,
    readonly siteHelper: SiteHelperService,
    readonly spinnerService: SpinnerService,
    readonly siteService: SiteService,
    public dialog: MatDialog
  ) {
    this.siteId = +this.route.snapshot.params.id;
    this.createForms();
  }

  ngOnInit() {
    this.store
      .select(selectActiveSite)
      .pipe(
        take(1),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(activeSite => {
        if (!activeSite) {
          this.router.navigate([`/site/${this.siteId}/map-site`]);
        } else {
          this.activeSite = activeSite;
          this.store.dispatch(
            updateSiteStep({
              id: this.activeSite.id,
              step: 'Documentation',
            })
          );
          this.valuePerYear = this.formatHelper.formatMinMaxValuation(
            this.activeSite.getAverageAnnualRevenue(this.activeSite.cohort.id)
          );
          if (!this.activeSite.name.startsWith('New Site -')) {
            this.submitInfoForm.controls.siteName.setValue(this.activeSite.name);
          }
          this.submitInfoForm.controls.ownerNames.setValue(this.activeSite.namesOnDeed);
          this.submitInfoForm.controls.mortgageInformation.setValue(this.activeSite.mortgageInformation);
          this.submitInfoForm.controls.forestryFirm.setValue(this.activeSite.forestryFirm);
          if (this.activeSite.mortgageInformation !== null) {
            this.showMortgage = true;
          }
          if (this.activeSite.namesOnDeed !== null) {
            this.showNameList = true;
          }
          if (this.activeSite.forestryFirm !== null) {
            this.showForestryFirm = true;
          }
        }
      });

    this.store
      .select(selectDocumentTypes)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(docTypes => {
        this.docTypes = docTypes
          ? docTypes.filter(t => !t.isPrivate).map(t => ({ value: t.id, viewValue: t.name }))
          : null;
        this.deedTypeId = docTypes ? docTypes.filter(d => d.name === 'Deed').map(d => d.id)[0] : null;
        this.taxTypeId = docTypes ? docTypes.filter(d => d.name === 'Tax Bill').map(d => d.id)[0] : null;
      });

    this.store
      .select('document', 'documents')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(documents => {
        if (documents && this.activeSite && documents[this.activeSite.id]) {
          this.existingDocs = documents[this.activeSite.id]
            .filter(d => this.docTypes.map(dt => dt.value).includes(d.documentTypeId))
            .map(d => cloneDeep(d));
        }
      });

    this.submitInfoForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.setIsFormDirty();
    });
  }

  createForms(): void {
    this.submitInfoForm = this.formBuilder.group({
      siteName: [null, [Validators.required, Validators.maxLength(50), Validators.pattern(RegexConstants.SITE_NAME)]],
      ownerNames: null,
      forestryFirm: null,
      mortgageInformation: null,
    });
  }

  advance(navigateToDash: boolean): void {
    window.scrollTo(0, 0);
    this.spinnerService.show('app-spinner');
    const site = new Site({
      name: this.submitInfoForm.value.siteName,
      namesOnDeed: this.submitInfoForm.value.ownerNames,
      forestryFirm: this.submitInfoForm.value.forestryFirm,
      mortgageInformation: this.submitInfoForm.value.mortgageInformation,
    });
    this.siteService
      .updateSiteMetadata(this.activeSite.id, site)
      .pipe(finalize(() => this.spinnerService.hide('app-spinner')))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        savedSite => {
          this.submitInfoForm.markAsPristine();
          this.isFormDirty = false;
          if (navigateToDash) {
            this.dialog
              .open(DialogYesNoComponent, {
                autoFocus: false,
                data: {
                  title: 'Documentation Saved',
                  line1: 'Your documentation has been saved. You can return and finish your submission at another time',
                  buttonTextYes: 'Return to Dashboard',
                },
              })
              .afterClosed()
              .subscribe(() => {
                this.router.navigate([`/account/dashboard`]);
              });
          } else {
            this.store.dispatch(
              updateSubmitDocumentationSiteSuccessAndNavigate({
                data: savedSite,
              })
            );
          }
        },
        error => this.store.dispatch(updateSiteFailure({ error }))
      );
  }

  formIsValid(): boolean {
    const namesValid = !this.showNameList || !isEmpty(this.submitInfoForm.value.ownerNames);
    const forestryFirmValid = !this.showForestryFirm || !isEmpty(this.submitInfoForm.value.forestryFirm);
    return (
      this.hasDocType(this.deedTypeId) &&
      this.hasDocType(this.taxTypeId) &&
      namesValid &&
      forestryFirmValid &&
      this.submitInfoForm.valid
    );
  }

  hasDocType(docTypeId: number) {
    return (
      this.existingDocs.length > 0 &&
      this.existingDocs.filter(d => d.documentTypeId == docTypeId && d.name !== null && d.identifier !== null).length >
        0
    );
  }

  otherNameClick($event: MatRadioChange) {
    this.showNameList = $event.value;
    this.setIsFormDirty();
  }

  forestryFirmClick($event: MatRadioChange) {
    this.showForestryFirm = $event.value;
    this.setIsFormDirty();
  }

  mortgageClick($event: MatRadioChange) {
    this.showMortgage = $event.value;
    this.setIsFormDirty();
  }

  setIsFormDirty() {
    if (!this.isFormDirty) {
      this.isFormDirty = true;
    }
  }
}
